import React from "react";
import DestinationTemplate from "./DestinationTemplate";
import { MapPin, Star, Camera, Users } from "lucide-react";

const Ujjain = () => {
  const destinationData = {
    cityName: "Ujjain",
    stateName: "Madhya Pradesh", 
    heroTitle: "Sacred City of",
    heroSubtitle: "Experience the spiritual heart of India - home to Mahakaleshwar Jyotirlinga and the magnificent Kumbh Mela, where divinity meets devotion.",
    metaDescription: "Discover Ujjain's spiritual treasures with Aaoghumen. Visit Mahakaleshwar Temple, experience Kumbh Mela, explore ancient ghats and immerse in India's most sacred pilgrimage destination.",
    keywords: "ujjain travel guide, mahakaleshwar temple, ujjain tourism, kumbh mela ujjain, jyotirlinga ujjain, madhya pradesh pilgrimage, aaoghumen ujjain, ujjain temples, spiritual tourism india",
    bestTimeToVisit: "Oct - Mar",
    slug: "ujjain",
    
    attractions: [
      {
        name: "Mahakaleshwar Temple",
        description: "One of the 12 Jyotirlingas and most sacred Shiva temples in India, famous for its Bhasma Aarti performed with sacred ash.",
        rating: 4.9,
        category: "Temple"
      },
      {
        name: "Ram Ghat",
        description: "Sacred bathing ghat on the Shipra River where pilgrims perform religious rituals and witness mesmerizing evening aarti.",
        rating: 4.6,
        category: "Ghat"
      },
      {
        name: "Kal Bhairav Temple",
        description: "Unique temple dedicated to Kal Bhairav where devotees offer liquor as prasad, representing fearlessness and protection.",
        rating: 4.5,
        category: "Temple"
      },
      {
        name: "Harsiddhi Temple",
        description: "Ancient Shakti Peetha dedicated to Goddess Harsiddhi, known for its red walls and spiritual significance in Hindu mythology.",
        rating: 4.4,
        category: "Temple"
      },
      {
        name: "ISKCON Ujjain",
        description: "Beautiful modern temple complex with stunning architecture, peaceful gardens, and spiritual programs for devotees.",
        rating: 4.7,
        category: "Temple"
      },
      {
        name: "Vedh Shala Observatory",
        description: "Historic astronomical observatory built by Maharaja Jai Singh II, showcasing ancient Indian knowledge of astronomy.",
        rating: 4.3,
        category: "Heritage"
      }
    ],

    experiences: [
      {
        icon: <Star className="w-6 h-6" />,
        title: "Bhasma Aarti Experience",
        description: "Witness the unique early morning Bhasma Aarti at Mahakaleshwar Temple, a divine ritual performed with sacred ash.",
        duration: "2-3 hours"
      },
      {
        icon: <Users className="w-6 h-6" />,
        title: "Spiritual Walking Tour",
        description: "Visit multiple temples and ghats with expert guides explaining the religious significance and ancient traditions.",
        duration: "Full day"
      },
      {
        icon: <Camera className="w-6 h-6" />,
        title: "Shipra River Aarti",
        description: "Experience the magical evening aarti at Ram Ghat with thousands of diyas floating on the sacred Shipra River.",
        duration: "2 hours"
      },
      {
        icon: <MapPin className="w-6 h-6" />,
        title: "Kumbh Mela Experience",
        description: "During Kumbh Mela years, witness the world's largest religious gathering with millions of pilgrims and saints.",
        duration: "Special event"
      }
    ],

    howToReach: [
      {
        mode: "By Air",
        details: "Nearest airport is Indore (55 km away). Regular flights connect Indore to major Indian cities."
      },
      {
        mode: "By Train",
        details: "Ujjain Junction is well-connected to major cities. Direct trains from Delhi, Mumbai, Jaipur, and Bhopal."
      },
      {
        mode: "By Road", 
        details: "Well-connected by National Highway. Regular bus services from Indore, Bhopal, and other major cities."
      }
    ],

    localCuisine: [
      {
        name: "Prasadam",
        description: "Sacred food offerings from temples, especially the blessed prasad from Mahakaleshwar Temple with unique spiritual significance."
      },
      {
        name: "Ujjaini Poha",
        description: "Local version of flattened rice preparation with distinctive spices and accompaniments unique to Ujjain."
      },
      {
        name: "Mathri and Namkeen",
        description: "Traditional crispy snacks perfect with tea, available at local sweet shops and street vendors."
      },
      {
        name: "Malpua",
        description: "Sweet pancakes soaked in sugar syrup, especially popular during festivals and religious celebrations."
      }
    ],

    travelTips: [
      {
        title: "Temple Etiquette",
        description: "Dress modestly, remove shoes before entering temples. Photography may be restricted in certain areas."
      },
      {
        title: "Booking Bhasma Aarti",
        description: "Book Bhasma Aarti tickets online in advance. Arrive early for better darshan experience."
      },
      {
        title: "Accommodation",
        description: "Book hotels near Mahakaleshwar Temple for easy access. During Kumbh Mela, book well in advance."
      },
      {
        title: "Best Time for Darshan",
        description: "Early morning and evening are best for temple visits. Avoid peak festival times if seeking peaceful darshan."
      }
    ],

    nearbyPlaces: [
      {
        name: "Indore",
        distance: "55 km",
        rating: "4.5",
        slug: "indore"
      },
      {
        name: "Omkareshwar",
        distance: "65 km",
        rating: "4.6",
        slug: "omkareshwar"
      },
      {
        name: "Maheshwar",
        distance: "85 km",
        rating: "4.4",
        slug: "maheshwar"
      }
    ],

    seoContent: `Ujjain, one of India's seven sacred cities (Sapta Puri), holds immense spiritual significance as the home of Mahakaleshwar Jyotirlinga. This ancient city on the banks of the Shipra River has been a center of learning, astronomy, and spirituality for over 2000 years. The famous Kumbh Mela held here every 12 years attracts millions of devotees from around the world. With Aaoghumen's knowledgeable guides, experience the divine atmosphere, participate in ancient rituals, and understand the deep spiritual traditions that make Ujjain a must-visit pilgrimage destination. From the mesmerizing Bhasma Aarti to the peaceful evening prayers at Ram Ghat, every moment in Ujjain connects you with India's rich spiritual heritage.`
  };

  return <DestinationTemplate {...destinationData} />;
};

export default Ujjain;